import { useState } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Sparkles, Copy, Check, RotateCcw, FileText, BookOpen, Link2 } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

function CodeBlock({ inline, className, children, ...props }) {
  const match = /language-(\w+)/.exec(className || "");
  const text = String(children).replace(/\n$/, "");
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (_) {}
  };

  if (inline) {
    return (
      <code className="rounded bg-white/10 px-1 py-0.5 font-mono text-[0.85em] text-fuchsia-200" {...props}>
        {children}
      </code>
    );
  }
  return (
    <div className="my-3 overflow-hidden rounded-xl border border-white/10 bg-[#0a0a0a]">
      <div className="flex items-center justify-between border-b border-white/5 bg-white/[0.02] px-3 py-1.5 text-xs text-white/45">
        <span className="font-mono">{match?.[1] || "text"}</span>
        <button
          onClick={copy}
          data-testid="code-copy-button"
          className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-white/55 transition hover:bg-white/5 hover:text-white"
        >
          {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <SyntaxHighlighter
        language={match?.[1] || "text"}
        style={oneDark}
        customStyle={{ background: "transparent", margin: 0, padding: "0.85rem 1rem", fontSize: "0.85rem" }}
        PreTag="div"
      >
        {text}
      </SyntaxHighlighter>
    </div>
  );
}

export function MessageBubble({ message, isLast, onRegenerate, modelDisplayName, streaming = false }) {
  const isUser = message.role === "user";
  const [copied, setCopied] = useState(false);

  const copyMessage = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (_) {}
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}
      className={`group flex w-full gap-4 px-1 py-5 ${isUser ? "justify-end" : "justify-start"}`}
      data-testid={`message-${message.role}-${message.id || "stream"}`}
    >
      {!isUser && (
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl brand-bg">
          <Sparkles className="h-4 w-4 text-white" />
        </div>
      )}

      <div className={`flex max-w-[78%] flex-col ${isUser ? "items-end" : "items-start"}`}>
        <div
          className={[
            "rounded-2xl px-4 py-3 text-[15px] leading-relaxed",
            isUser
              ? "brand-gradient bg-gradient-to-br from-indigo-500/85 via-violet-500/80 to-fuchsia-500/85 text-white shadow-lg shadow-fuchsia-500/10"
              : "glass text-white/90",
          ].join(" ")}
        >
          {message.file_ids?.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-1.5">
              {message.attached_files?.map((f) => (
                <span key={f.id} className="inline-flex items-center gap-1 rounded-md border border-white/15 bg-white/10 px-2 py-0.5 text-xs">
                  <FileText className="h-3 w-3" /> {f.filename}
                </span>
              ))}
            </div>
          )}

          {isUser ? (
            <div className="whitespace-pre-wrap">{message.content}</div>
          ) : (
            <div className="prose-invert">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  code: CodeBlock,
                  p: ({ children }) => <p className="my-2 first:mt-0 last:mb-0 leading-relaxed">{children}</p>,
                  ul: ({ children }) => <ul className="my-2 list-disc pl-5 space-y-1">{children}</ul>,
                  ol: ({ children }) => <ol className="my-2 list-decimal pl-5 space-y-1">{children}</ol>,
                  h1: ({ children }) => <h1 className="font-display mt-3 mb-2 text-xl font-medium">{children}</h1>,
                  h2: ({ children }) => <h2 className="font-display mt-3 mb-2 text-lg font-medium">{children}</h2>,
                  h3: ({ children }) => <h3 className="font-display mt-3 mb-2 text-base font-medium">{children}</h3>,
                  a: ({ children, ...props }) => <a className="text-fuchsia-300 underline-offset-4 hover:underline" {...props}>{children}</a>,
                  blockquote: ({ children }) => <blockquote className="my-3 border-l-2 border-fuchsia-500/40 bg-white/[0.02] px-3 py-1 text-white/75">{children}</blockquote>,
                  table: ({ children }) => <div className="my-3 overflow-x-auto rounded-lg border border-white/10"><table className="w-full text-sm">{children}</table></div>,
                  th: ({ children }) => <th className="border-b border-white/10 bg-white/[0.03] px-3 py-2 text-left text-xs uppercase tracking-wider text-white/50">{children}</th>,
                  td: ({ children }) => <td className="border-b border-white/5 px-3 py-2">{children}</td>,
                }}
              >
                {message.content}
              </ReactMarkdown>
              {streaming && <span className="ml-0.5 inline-block h-3 w-1.5 animate-pulse rounded-sm bg-white/70" />}
            </div>
          )}

          {message.kb_indicator && message.kb_indicator.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-white/10 pt-2.5 text-xs text-white/55" data-testid="msg-sources-footer">
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-white/40">
                <BookOpen className="h-3 w-3 text-fuchsia-400" /> Sources
              </span>
              {message.kb_indicator.slice(0, 5).map((k, i) => {
                // Iter 23 (audit D2) — per UX spec: chip label is doc title
                // truncated to ~30 chars with ellipsis; full title in
                // `title` attribute for hover tooltip. Muted gray fill
                // with subtle hover (slate, not fuchsia) to keep it low-
                // contrast relative to the assistant content above.
                const fullLabel = k.filename || (k.document_id ? `${k.document_id.slice(0, 8)}…` : "doc");
                const label = fullLabel.length > 30 ? `${fullLabel.slice(0, 28)}…` : fullLabel;
                const to = k.document_id ? `/app/knowledge?doc=${k.document_id}` : "/app/knowledge";
                return (
                  <Link
                    key={k.document_id || i}
                    to={to}
                    title={fullLabel}
                    className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.06] px-2 py-0.5 text-[11px] text-white/65 transition hover:bg-white/10 hover:text-white"
                    data-testid={`msg-source-chip-${k.document_id || i}`}
                  >
                    <Link2 className="h-2.5 w-2.5 text-fuchsia-300/80" /> {label}
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        {!isUser && !streaming && (
          <div className="mt-1.5 flex items-center gap-1 opacity-0 transition group-hover:opacity-100">
            {message.model_id && (
              <span className="mr-1 rounded-full border border-white/10 bg-white/5 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-white/40">
                {modelDisplayName || message.model_id}
              </span>
            )}
            <button
              onClick={copyMessage}
              data-testid={`message-copy-${message.id}`}
              className="rounded p-1 text-white/40 hover:bg-white/5 hover:text-white"
            >
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            </button>
            {isLast && onRegenerate && (
              <button
                onClick={onRegenerate}
                data-testid={`message-regenerate-${message.id}`}
                className="rounded p-1 text-white/40 hover:bg-white/5 hover:text-white"
              >
                <RotateCcw className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}
